/* eslint-disable no-unsafe-optional-chaining */
import { ArrowDownIcon, ArrowUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  HStack,
  Avatar,
  Text,
  Heading,
  Stack,
  Spacer,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Badge,
  useToast,
  Spinner,
  Center,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Icon,
  VStack,
  Select,
  AvatarGroup,
  Tooltip,
  Divider,
  Grid,
  GridItem,
  Progress,
} from '@chakra-ui/react';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButtons from '../../Components/Buttons/BackButtons';
import useUserStore from '../../Hooks/Zustand/Store';
import { msToTime } from '../../Utils/timeUtil';
import DatePicker from '../../Components/DatePicker/DatePicker';
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md';
import { FaRegCalendar, FaTrophy, FaMedal, FaAward, FaClock, FaTasks } from 'react-icons/fa';
import { TbReload } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  getAggregateFromServer,
  sum,
  average,
  count,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../Config/firebase';

function ManagementScoreTaskV2() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [sort, setSort] = useState({ sort: 'totalScore', desc: true });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProject, setSelectedProject] = useState('');
  const [statistics, setStatistics] = useState({
    totalBoards: 0,
    totalTasks: 0,
    totalScoreAll: 0,
    avgScoreAll: 0,
    avgTimeAll: 0,
    topPerformer: null,
  });
  const itemsPerPage = 10;

  const dates = useRef({
    startDate: moment().subtract(30, 'days').unix(),
    endDate: moment().unix(),
  });

  const globalState = useUserStore();
  const toast = useToast();
  const navigate = useNavigate();

  const getScoreboardData = async () => {
    if (!globalState.currentCompany) return;

    setLoading(true);
    try {
      const { startDate, endDate } = dates.current;
      
      const startTimestamp = Timestamp.fromMillis(startDate * 1000);
      const endTimestamp = Timestamp.fromMillis(endDate * 1000);

      const filesRef = collection(db, 'files');
      let filesQuery = query(
        filesRef,
        where('companyId', '==', globalState.currentCompany),
        where('type', '==', 'kanban')
      );

      if (selectedProject) {
        filesQuery = query(
          filesQuery,
          where('projectId', '==', selectedProject)
        );
      }

      const filesSnapshot = await getDocs(filesQuery);
      
      if (filesSnapshot.empty) {
        setData([]);
        setTotalPages(1);
        setStatistics({
          totalBoards: 0,
          totalTasks: 0,
          totalScoreAll: 0,
          avgScoreAll: 0,
          avgTimeAll: 0,
          topPerformer: null,
        });
        toast({
          title: 'Info',
          description: 'No kanban boards found',
          status: 'info',
          duration: 3000,
          position: 'top-right',
        });
        return;
      }

      const scoreboardPromises = filesSnapshot.docs.map(async (fileDoc) => {
        const fileData = fileDoc.data();
        const filesId = fileDoc.id;

        const kanbanRef = collection(db, 'kanban');
        const kanbanQuery = query(
          kanbanRef,
          where('filesId', '==', filesId),
          where('companyId', '==', globalState.currentCompany),
          where('column', '==', 'done'),  // ← ONLY DONE TASKS
          where('lastUpdated', '>=', startTimestamp),
          where('lastUpdated', '<=', endTimestamp)
        );

        try {
          const aggregateSnapshot = await getAggregateFromServer(kanbanQuery, {
            totalScore: sum('score'),
            avgScore: average('score'),
            taskCount: count()
          });

          const aggregateData = aggregateSnapshot.data();
          
          const tasksSnapshot = await getDocs(kanbanQuery);
          let totalTime = 0;
          let taskWithTime = 0;

          tasksSnapshot.forEach((doc) => {
            const task = doc.data();
            
            if (task.time_completed_at && task.time_backlog_at) {
              const completedTime = task.time_completed_at.seconds;
              const backlogTime = task.time_backlog_at.seconds;
              const timeSpent = (completedTime - backlogTime) * 1000;
              
              if (timeSpent > 0) {
                totalTime += timeSpent;
                taskWithTime++;
              }
            }
          });

          const avgTime = taskWithTime > 0 ? totalTime / taskWithTime : 0;
          const taskCount = tasksSnapshot.size;

          const project = globalState.projects?.find((p) => p.id === fileData.projectId);

          return {
            id: filesId,
            title: fileData.title || 'Untitled',
            image: fileData.image || '',
            avgScore: aggregateData.avgScore || 0,
            totalScore: aggregateData.totalScore || 0,
            tasks: taskCount,
            avgTime: avgTime,
            projectId: fileData.projectId,
            projectName: project?.name || 'Unknown Project',
            usersDisplay: fileData.usersDisplay || [],
          };
        } catch (error) {
          console.error(`Error processing file ${filesId}:`, error);
          return null;
        }
      });

      let results = await Promise.all(scoreboardPromises);

      console.log(results, 'ini results')
      
      results = results.filter((item) => item !== null && item.tasks > 0);

      const totalTasks = results.reduce((sum, item) => sum + item.tasks, 0);
      const totalScoreAll = results.reduce((sum, item) => sum + item.totalScore, 0);
      const avgScoreAll = totalTasks > 0 ? totalScoreAll / totalTasks : 0;
      const totalTime = results.reduce((sum, item) => sum + (item.avgTime * item.tasks), 0);
      const avgTimeAll = totalTasks > 0 ? totalTime / totalTasks : 0;
      
      const topPerformer = results.length > 0 ? results.reduce((max, item) => 
        item.totalScore > max.totalScore ? item : max
      ) : null;

      setStatistics({
        totalBoards: results.length,
        totalTasks: totalTasks,
        totalScoreAll: totalScoreAll,
        avgScoreAll: avgScoreAll,
        avgTimeAll: avgTimeAll,
        topPerformer: topPerformer,
      });

      results.sort((a, b) => {
        const multiplier = sort.desc ? -1 : 1;
        if (sort.sort === 'totalScore') {
          return multiplier * (a.totalScore - b.totalScore);
        } else if (sort.sort === 'avgScore') {
          return multiplier * (a.avgScore - b.avgScore);
        } else if (sort.sort === 'tasks') {
          return multiplier * (a.tasks - b.tasks);
        } else if (sort.sort === 'avgTime') {
          return multiplier * (a.avgTime - b.avgTime);
        }
        return 0;
      });

      const total = Math.ceil(results.length / itemsPerPage);
      setTotalPages(total);

      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedData = results.slice(startIndex, endIndex);

      setData(paginatedData);
      
      if (paginatedData.length === 0 && results.length > 0) {
        setPage(1);
      }

    } catch (error) {
      console.error('Error fetching scoreboard data:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch scoreboard data',
        status: 'error',
        duration: 5000,
        position: 'top-right',
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (sortField) => {
    const newSort = {
      sort: sortField,
      desc: sort.sort === sortField ? !sort.desc : true
    };
    setSort(newSort);
  };

  const onDateChange = (data) => {
    dates.current = {
      startDate: moment(data.startDate).unix(),
      endDate: moment(data.endDate).unix(),
    };
  };

  const handleReloadFilter = () => {
    dates.current = {
      startDate: moment().subtract(30, 'days').unix(),
      endDate: moment().unix(),
    };
    setSelectedProject('');
    setPage(1);
    getScoreboardData();
  };

  const getRankBadge = (index) => {
    if (index === 0) return { icon: FaTrophy, color: 'yellow.400', label: '🥇 1st' };
    if (index === 1) return { icon: FaMedal, color: 'gray.400', label: '🥈 2nd' };
    if (index === 2) return { icon: FaAward, color: 'orange.600', label: '🥉 3rd' };
    return { icon: null, color: 'gray.600', label: `#${index + 1}` };
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'green';
    if (score >= 7) return 'blue';
    if (score >= 6) return 'yellow';
    if (score >= 5) return 'orange';
    return 'red';
  };

  const getScoreLabel = (score) => {
    if (score >= 8) return 'Excellent';
    if (score >= 7) return 'Good';
    if (score >= 6) return 'Average';
    if (score >= 5) return 'Below Average';
    return 'Need Improvement';
  };

  const handleSeeHistory = (item) => {
    navigate(`/management/score/tasks/${item.id}`);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  useEffect(() => {
    if (globalState.currentCompany) {
      getScoreboardData();
    }
  }, [globalState.currentCompany, page, sort, selectedProject]);

  useEffect(() => {
    return () => {
      setData([]);
      setPage(1);
    };
  }, []);

  return (
    <Stack p={[2, 3, 5]} spacing={5}>
      {/* Header Section */}
      <HStack flexWrap="wrap" spacing={3}>
        <BackButtons />
        <VStack align="start" spacing={0}>
          <Heading size={'lg'} bgGradient="linear(to-r, blue.400, purple.500)" bgClip="text">
            🏆 {t('score-board')}
          </Heading>
          <Text fontSize="sm" color="gray.500">Performance Analytics Dashboard</Text>
        </VStack>
        <Spacer />
        <HStack>
          <Icon as={FaRegCalendar} color="blue.500" />
          <Text fontSize="sm" color="gray.600" fontWeight="medium">
            {moment.unix(dates.current.startDate).format('MMM DD')} - {moment.unix(dates.current.endDate).format('MMM DD, YYYY')}
          </Text>
        </HStack>
      </HStack>

      {/* Statistics Cards */}
      <SimpleGrid columns={[1, 2, 4]} spacing={4}>
        <Card bgGradient="linear(to-br, blue.400, blue.600)" color="white" shadow="lg">
          <CardBody>
            <HStack>
              <Icon as={FaTasks} boxSize={8} />
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" opacity={0.9}>Total Boards</Text>
                <Heading size="lg">{statistics.totalBoards}</Heading>
              </VStack>
            </HStack>
          </CardBody>
        </Card>

        <Card bgGradient="linear(to-br, purple.400, purple.600)" color="white" shadow="lg">
          <CardBody>
            <HStack>
              <Icon as={FaTasks} boxSize={8} />
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" opacity={0.9}>Completed Tasks</Text>
                <Heading size="lg">{statistics.totalTasks}</Heading>
                <Text fontSize="xs" opacity={0.8}>✅ Done Status</Text>
              </VStack>
            </HStack>
          </CardBody>
        </Card>

        <Card bgGradient="linear(to-br, green.400, green.600)" color="white" shadow="lg">
          <CardBody>
            <HStack>
              <Icon as={FaTrophy} boxSize={8} />
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" opacity={0.9}>Total Score</Text>
                <Heading size="xl">{statistics.totalScoreAll? (statistics.totalScoreAll.toFixed(0)) : 0}</Heading>
                <Text fontSize="xs" opacity={0.8}>Avg: {statistics.avgScoreAll.toFixed(2)}</Text>
              </VStack>
            </HStack>
          </CardBody>
        </Card>

        <Card bgGradient="linear(to-br, orange.400, orange.600)" color="white" shadow="lg">
          <CardBody>
            <HStack>
              <Icon as={FaClock} boxSize={8} />
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" opacity={0.9}>Avg Completion</Text>
                <Heading size="lg">{msToTime(statistics.avgTimeAll)}</Heading>
              </VStack>
            </HStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Top Performer Card */}
      {statistics.topPerformer && (
        <Card bg="yellow.50" borderWidth={2} borderColor="yellow.400" shadow="md">
          <CardHeader pb={2}>
            <HStack>
              <Icon as={FaTrophy} color="yellow.500" boxSize={6} />
              <Heading size="md" color="yellow.700">🎯 Top Performer - Ranked by Total Score</Heading>
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            <HStack spacing={4}>
              <Avatar size="lg" name={statistics.topPerformer.title} src={statistics.topPerformer.image} />
              <VStack align="start" spacing={1}>
                <Text fontWeight="bold" fontSize="lg">{statistics.topPerformer.title}</Text>
                <HStack spacing={2}>
                  <Badge colorScheme="yellow" fontSize="md" px={3} py={1}>
                    🏆 Total: {statistics.topPerformer.totalScore.toFixed(0)} pts
                  </Badge>
                  <Badge colorScheme={getScoreColor(statistics.topPerformer.avgScore)} fontSize="md" px={3} py={1}>
                    Avg: {statistics.topPerformer.avgScore.toFixed(2)}
                  </Badge>
                  <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                    ✅ {statistics.topPerformer.tasks} Done
                  </Badge>
                </HStack>
              </VStack>
              <Spacer />
              <Icon as={FaTrophy} color="yellow.400" boxSize={12} />
            </HStack>
          </CardBody>
        </Card>
      )}

      {/* Filters Section */}
      <Card shadow="md">
        <CardBody>
          <Grid templateColumns={['1fr', '1fr', 'repeat(3, 1fr)']} gap={3}>
            <GridItem>
              <Select
                placeholder="🎯 Select Project"
                value={selectedProject}
                onChange={(e) => {
                  setSelectedProject(e.target.value);
                  setPage(1);
                }}
                size="md"
                bg="white"
              >
                {globalState.projects?.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </Select>
            </GridItem>

            <GridItem>
              <Button
                onClick={onOpen}
                w="full"
                leftIcon={<FaRegCalendar />}
                colorScheme="blue"
                variant="outline"
              >
                {t('filterDate')}
              </Button>
            </GridItem>

            <GridItem>
              <Button
                onClick={handleReloadFilter}
                w="full"
                leftIcon={<TbReload />}
                colorScheme="purple"
                isDisabled={loading}
              >
                Reset Filters
              </Button>
            </GridItem>
          </Grid>
        </CardBody>
      </Card>

      {/* Data Table */}
      <Card shadow="lg">
        <CardHeader>
          <VStack align="start" spacing={1}>
            <Heading size="md">📊 Detailed Rankings (Sorted by Total Score)</Heading>
          </VStack>
        </CardHeader>
        <CardBody pt={0}>
          {loading ? (
            <Center py={10}>
              <VStack>
                <Spinner size="xl" color="blue.500" thickness="4px" />
                <Text color="gray.500">Loading scoreboard...</Text>
              </VStack>
            </Center>
          ) : (
            <>
              <TableContainer>
                <Table variant="simple" size="md">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Rank</Th>
                      <Th>Board Name</Th>
                      <Th>Project</Th>
                      <Th>Team</Th>
                      <Th cursor="pointer" onClick={() => handleSort('totalScore')}>
                        <HStack spacing={1}>
                          <Text>Total Score</Text>
                          {sort.sort === 'totalScore' && (
                            sort.desc ? <ArrowDownIcon /> : <ArrowUpIcon />
                          )}
                        </HStack>
                      </Th>
                      <Th cursor="pointer" onClick={() => handleSort('avgScore')}>
                        <HStack spacing={1}>
                          <Text>Avg Score</Text>
                          {sort.sort === 'avgScore' && (
                            sort.desc ? <ArrowDownIcon /> : <ArrowUpIcon />
                          )}
                        </HStack>
                      </Th>
                      <Th cursor="pointer" onClick={() => handleSort('tasks')}>
                        <HStack spacing={1}>
                          <Text>Done Tasks</Text>
                          {sort.sort === 'tasks' && (
                            sort.desc ? <ArrowDownIcon /> : <ArrowUpIcon />
                          )}
                        </HStack>
                      </Th>
                      <Th cursor="pointer" onClick={() => handleSort('avgTime')}>
                        <HStack spacing={1}>
                          <Text>Avg Time</Text>
                          {sort.sort === 'avgTime' && (
                            sort.desc ? <ArrowDownIcon /> : <ArrowUpIcon />
                          )}
                        </HStack>
                      </Th>
                      <Th>Action</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.length === 0 ? (
                      <Tr>
                        <Td colSpan={9}>
                          <Center py={10}>
                            <VStack>
                              <Text fontSize="4xl">📭</Text>
                              <Text color="gray.500" fontWeight="medium">No data available</Text>
                              <Text color="gray.400" fontSize="sm">Try adjusting your filters</Text>
                            </VStack>
                          </Center>
                        </Td>
                      </Tr>
                    ) : (
                      data.map((item, index) => {
                        const globalIndex = (page - 1) * itemsPerPage + index;
                        const rankInfo = getRankBadge(globalIndex);
                        
                        return (
                          <Tr 
                            key={item.id} 
                            _hover={{ bg: 'gray.50' }}
                            transition="all 0.2s"
                          >
                            <Td>
                              <HStack>
                                {rankInfo.icon && (
                                  <Icon as={rankInfo.icon} color={rankInfo.color} boxSize={5} />
                                )}
                                <Badge 
                                  colorScheme={globalIndex < 3 ? 'yellow' : 'gray'}
                                  fontSize="sm"
                                  px={2}
                                >
                                  {rankInfo.label}
                                </Badge>
                              </HStack>
                            </Td>
                            <Td>
                              <HStack spacing={3}>
                                <Avatar 
                                  size="md" 
                                  name={item.title} 
                                  src={item.image || 'https://bit.ly/broken-link'} 
                                />
                                <VStack align="start" spacing={0}>
                                  <Text fontWeight="bold" fontSize="sm">
                                    {item.title}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    ID: {item.id.substring(0, 8)}...
                                  </Text>
                                </VStack>
                              </HStack>
                            </Td>
                            <Td>
                              <Badge colorScheme="blue" fontSize="xs">
                                {item.projectName}
                              </Badge>
                            </Td>
                            <Td>
                              <Tooltip label={`${item.usersDisplay?.length || 0} team members`}>
                                <Box>
                                  <AvatarGroup size="sm" max={3}>
                                    {item.usersDisplay?.slice(0, 5).map((user, i) => (
                                      <Avatar 
                                        key={i} 
                                        name={user.name || user.email} 
                                        src={user.image}
                                      />
                                    ))}
                                  </AvatarGroup>
                                </Box>
                              </Tooltip>
                            </Td>
                            <Td>
                              <VStack align="start" spacing={1}>
                                <Badge
                                  colorScheme="yellow"
                                  fontSize="lg"
                                  px={3}
                                  py={1}
                                  fontWeight="bold"
                                >
                                  🏆 {item.totalScore.toFixed(0)} pts
                                </Badge>
                                <Tooltip label={`${((item.totalScore / statistics.totalScoreAll) * 100).toFixed(1)}% of total`}>
                                  <Box w="full">
                                    <Progress 
                                      value={(item.totalScore / statistics.totalScoreAll) * 100} 
                                      size="xs" 
                                      colorScheme="yellow"
                                      w="80px"
                                      borderRadius="full"
                                    />
                                  </Box>
                                </Tooltip>
                              </VStack>
                            </Td>
                            <Td>
                              <VStack align="start" spacing={1}>
                                <Badge
                                  colorScheme={getScoreColor(item.avgScore)}
                                  fontSize="md"
                                  px={3}
                                  py={1}
                                >
                                  {item.avgScore.toFixed(2)}
                                </Badge>
                                <Text fontSize="xs" color="gray.500">
                                  {getScoreLabel(item.avgScore)}
                                </Text>
                              </VStack>
                            </Td>
                            <Td>
                              <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
                                {item.tasks}
                              </Badge>
                            </Td>
                            <Td>
                              <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                                {msToTime(item.avgTime)}
                              </Badge>
                            </Td>
                            <Td>
                              <Button
                                size="sm"
                                colorScheme="blue"
                                variant="outline"
                                onClick={() => handleSeeHistory(item)}
                              >
                                View
                              </Button>
                            </Td>
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </Table>
              </TableContainer>

              {data.length > 0 && totalPages > 1 && (
                <Divider my={4} />
              )}
              
              {data.length > 0 && totalPages > 1 && (
                <HStack justifyContent="center" spacing={4}>
                  <Button
                    leftIcon={<MdNavigateBefore />}
                    colorScheme="blue"
                    size="sm"
                    onClick={() => handlePageChange(page - 1)}
                    isDisabled={page <= 1}
                  >
                    {t('prev')}
                  </Button>

                  <HStack spacing={2}>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          colorScheme={page === pageNum ? 'blue' : 'gray'}
                          variant={page === pageNum ? 'solid' : 'outline'}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </HStack>

                  <Button
                    rightIcon={<MdNavigateNext />}
                    colorScheme="blue"
                    size="sm"
                    onClick={() => handlePageChange(page + 1)}
                    isDisabled={page >= totalPages}
                  >
                    {t('next')}
                  </Button>
                </HStack>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Date Filter Modal */}
      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Icon as={FaRegCalendar} color="blue.500" />
              <Text>{t('select')} {t('date')}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <DatePicker onDateChange={onDateChange} />
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button colorScheme="gray" onClick={onClose}>
                {t('close')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => {
                  setPage(1);
                  getScoreboardData();
                  onClose();
                }}
              >
                Apply Filter
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
}

export default ManagementScoreTaskV2;